import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Search, SlidersHorizontal, X } from 'lucide-react';
import { Product } from '@/lib/index';
import { ProductCard } from '@/components/ProductCard';
import { useProducts, useCategories, useBrands } from '@/hooks/useSupabaseData';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';

export default function Products() {
  const { products, loading: productsLoading } = useProducts();
  const { categories, loading: categoriesLoading } = useCategories();
  const { brands, loading: brandsLoading } = useBrands();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedBrand, setSelectedBrand] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'price-asc' | 'price-desc' | 'name-asc' | 'rating-desc'>('name-asc');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    // Filter by brand
    if (selectedBrand !== 'all') {
      filtered = filtered.filter((p) => p.brand === selectedBrand);
    }

    // Filter by search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.brand.toLowerCase().includes(query)
      );
    }

    // Sort
    if (sortBy === 'price-asc') {
      filtered.sort((a, b) => a.price - b.price);
    } else if (sortBy === 'price-desc') {
      filtered.sort((a, b) => b.price - a.price);
    } else if (sortBy === 'name-asc') {
      filtered.sort((a, b) => a.name.localeCompare(b.name, 'ar'));
    } else if (sortBy === 'rating-desc') {
      filtered.sort((a, b) => b.rating - a.rating);
    }

    return filtered;
  }, [products, searchQuery, selectedCategory, selectedBrand, sortBy]);

  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('all');
    setSelectedBrand('all');
    setSortBy('name-asc');
  };

  const hasActiveFilters = searchQuery || selectedCategory !== 'all' || selectedBrand !== 'all' || sortBy !== 'name-asc';

  const FilterContent = () => (
    <div className="space-y-6" dir="rtl">
      {/* الفئات */}
      <div>
        <h3 className="text-lg font-bold mb-4 text-foreground">الفئات</h3>
        <div className="space-y-2">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'ghost'}
            className="w-full justify-start text-base h-11"
            onClick={() => {
              setSelectedCategory('all');
              setMobileFiltersOpen(false);
            }}
          >
            جميع المنتجات
            {selectedCategory === 'all' && (
              <Badge className="mr-auto" variant="secondary">
                {products.length}
              </Badge>
            )}
          </Button>
          {categories.map((category) => {
            const count = products.filter((p) => p.category === category).length;
            return (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'ghost'}
                className="w-full justify-start text-base h-11"
                onClick={() => {
                  setSelectedCategory(category);
                  setMobileFiltersOpen(false);
                }}
              >
                {category}
                <Badge className="mr-auto" variant={selectedCategory === category ? 'secondary' : 'outline'}>
                  {count}
                </Badge>
              </Button>
            );
          })}
        </div>
      </div>

      {/* الماركات */}
      <div>
        <h3 className="text-lg font-bold mb-4 text-foreground">الماركات</h3>
        <Select value={selectedBrand} onValueChange={(value) => {
          setSelectedBrand(value);
          setMobileFiltersOpen(false);
        }}>
          <SelectTrigger className="w-full h-11">
            <SelectValue placeholder="اختر الماركة" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الماركات ({brands.length})</SelectItem>
            {brands.map((brand) => {
              const count = products.filter((p) => p.brand === brand).length;
              return (
                <SelectItem key={brand} value={brand}>
                  {brand} ({count})
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-4 text-right">جميع المنتجات</h1>
          <p className="text-muted-foreground text-right">
            اكتشف مجموعتنا الكاملة من المنتجات عالية الجودة
          </p>
        </motion.div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Desktop Filters */}
          <aside className="hidden lg:block w-72 flex-shrink-0">
            <div className="sticky top-24 space-y-6 p-6 bg-card rounded-lg border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">الفلاتر</h2>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleClearFilters}
                  >
                    <X className="w-4 h-4 ml-1" />
                    مسح
                  </Button>
                )}
              </div>
              <FilterContent />
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Search and Sort Bar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6" dir="rtl">
              <div className="relative flex-1">
                <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="ابحث عن منتج..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pr-10"
                />
              </div>

              <Select value={sortBy} onValueChange={(value: any) => setSortBy(value)}>
                <SelectTrigger className="w-full sm:w-[200px]">
                  <SelectValue placeholder="ترتيب حسب" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="name-asc">الاسم (أ-ي)</SelectItem>
                  <SelectItem value="price-asc">السعر (الأقل أولاً)</SelectItem>
                  <SelectItem value="price-desc">السعر (الأعلى أولاً)</SelectItem>
                  <SelectItem value="rating-desc">الأعلى تقييماً</SelectItem>
                </SelectContent>
              </Select>

              {/* Mobile Filter Button */}
              <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                <SheetTrigger asChild>
                  <Button variant="default" className="lg:hidden h-11 min-w-[120px]">
                    <SlidersHorizontal className="w-5 h-5 ml-2" />
                    فلاتر
                    {hasActiveFilters && (
                      <Badge className="mr-2 bg-white text-primary">
                        {(selectedCategory !== 'all' ? 1 : 0) + (selectedBrand !== 'all' ? 1 : 0)}
                      </Badge>
                    )}
                  </Button>
                </SheetTrigger>
                <SheetContent side="right" className="w-[85vw] sm:w-[400px]">
                  <SheetHeader className="mb-6">
                    <SheetTitle className="text-right text-2xl font-bold">فلاتر البحث</SheetTitle>
                  </SheetHeader>
                  <div className="space-y-6">
                    <FilterContent />
                    {hasActiveFilters && (
                      <Button
                        variant="outline"
                        className="w-full h-11"
                        onClick={() => {
                          handleClearFilters();
                          setMobileFiltersOpen(false);
                        }}
                      >
                        <X className="w-4 h-4 ml-2" />
                        مسح جميع الفلاتر
                      </Button>
                    )}
                    <Button
                      className="w-full h-11"
                      onClick={() => setMobileFiltersOpen(false)}
                    >
                      عرض النتائج ({filteredProducts.length})
                    </Button>
                  </div>
                </SheetContent>
              </Sheet>
            </div>

            {/* Active Filters */}
            {hasActiveFilters && (
              <div className="flex flex-wrap gap-2 mb-6" dir="rtl">
                {selectedCategory !== 'all' && (
                  <Badge variant="secondary" className="gap-2">
                    {selectedCategory}
                    <button
                      onClick={() => setSelectedCategory('all')}
                      className="hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {selectedBrand !== 'all' && (
                  <Badge variant="secondary" className="gap-2">
                    {selectedBrand}
                    <button
                      onClick={() => setSelectedBrand('all')}
                      className="hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
                {searchQuery && (
                  <Badge variant="secondary" className="gap-2">
                    بحث: {searchQuery}
                    <button
                      onClick={() => setSearchQuery('')}
                      className="hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                )}
              </div>
            )}

            {/* Results Count */}
            <p className="text-sm text-muted-foreground mb-6 text-right">
              عرض {filteredProducts.length} من {products.length} منتج
            </p>

            {/* Products Grid */}
            {productsLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
                {Array.from({ length: 8 }).map((_, index) => (
                  <div key={index} className="bg-muted animate-pulse rounded-lg h-96" />
                ))}
              </div>
            ) : filteredProducts.length > 0 ? (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
              >
                {filteredProducts.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-24">
                <p className="text-2xl font-semibold mb-2">لا توجد منتجات</p>
                <p className="text-muted-foreground mb-6">
                  لم نجد أي منتجات تطابق معايير البحث
                </p>
                <Button onClick={handleClearFilters}>مسح الفلاتر</Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}